'use client';

import { useLayoutEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';
import { FileIcon, SpinnerGapIcon } from '@phosphor-icons/react';
import type { ForensicTreeNode } from '@tranquility-ai/theme-components';
import type { UfdrMediaItem } from '@/shared/ufdr/legacy-types';
import { fetchProvenanceFileInfo } from '@/shared/cors-provenance-api';
import { fileInfoFromProvenanceBody, getMediaType, isContainerLikeSource } from '@/shared/functions';
import { resolveUfdrOpenMediaUrl } from '@/shared/ufdr-export-adapter';
import type { MediaSource, Source } from '@/shared/types';
import { MediaDisplay } from '@/components/ui/mediaDisplay';
import { useCellServiceSession } from '@/hooks/useCellServiceSession';
import { useTimePilotT } from '@/i18n/useTimePilotT';

/**
 * In-viewer UFDR file. Same provenance and MediaDisplay path as an email
 * attachment, without a second overlay. The UFDR dialog already owns the surface.
 */
export function UfdrMediaInline({
  item,
}: {
  item: ForensicTreeNode | UfdrMediaItem;
}) {
  const t = useTimePilotT();
  const { cellId, accessToken, enabled } = useCellServiceSession();
  const searchParams = useSearchParams();
  // Evidence locker keeps the case on `case`. Older new-tab links used `caseId`.
  const caseId = searchParams.get('case') || searchParams.get('caseId');
  const url = resolveUfdrOpenMediaUrl(item);

  const bodyRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<{ width: number; height: number } | null>(null);

  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    const update = () => {
      const rect = el.getBoundingClientRect();
      setSize({ width: Math.floor(rect.width), height: Math.floor(rect.height) });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const { data: fileSummary, isLoading, error } = useQuery({
    queryKey: ['ufdr-media-file-info', cellId, url],
    queryFn: async ({ signal }) => {
      const body = await fetchProvenanceFileInfo({
        cellId: cellId as string,
        accessToken: accessToken as string,
        s3Url: url as string,
        signal,
      });
      return fileInfoFromProvenanceBody(body);
    },
    enabled: enabled && !!url,
  });

  const isContainer = !!url && isContainerLikeSource(
    { sourceFilePath: url } as Source,
    fileSummary,
  );

  const mediaItem: MediaSource | null = url
    ? {
        mediaUrl: url,
        mediaType: getMediaType({ mediaUrl: url } as MediaSource, fileSummary),
        fileInfo: fileSummary ?? null,
      }
    : null;

  return (
    <div
      ref={bodyRef}
      className="relative flex min-h-0 flex-1 flex-col overflow-hidden bg-white"
    >
      {!url ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-2 px-6 text-center">
          <FileIcon size={32} className="text-gray-400" />
          <div className="text-sm font-medium text-gray-800">{t('viewers.thisFileCanTBePreviewed')}</div>
          <div className="max-w-sm text-xs text-gray-500">
            {t('viewers.containerFilesArchivesMailboxesAndSimilarBundles')}
          </div>
        </div>
      ) : isLoading ? (
        <div className="flex flex-1 items-center justify-center text-sm text-gray-500">
          <SpinnerGapIcon size={20} className="mr-2 animate-spin" />
          {t('viewers.loadingFile')}
        </div>
      ) : error ? (
        <div className="flex flex-1 items-center justify-center px-6 text-center text-sm text-gray-600">
          {error instanceof Error ? error.message : t('viewers.weCouldnTLoadTheViewerPlease')}
        </div>
      ) : isContainer ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-2 px-6 text-center">
          <FileIcon size={32} className="text-gray-400" />
          <div className="text-sm font-medium text-gray-800">{t('viewers.thisFileCanTBePreviewed')}</div>
          <div className="max-w-sm text-xs text-gray-500">
            {t('viewers.containerFilesArchivesMailboxesAndSimilarBundles')}
          </div>
        </div>
      ) : (
        <MediaDisplay
          key={url}
          isPresentation={false}
          isSourceCard={true}
          caseId={caseId}
          hideMetaSocialViewerButton
          hideCaptureControls
          showToolbarExpandButton={false}
          mediaItem={mediaItem as MediaSource}
          containerWidth={size?.width}
          containerHeight={size?.height}
          fillContainer
        />
      )}
    </div>
  );
}
